// Normalize whatever an extractor sends for a multi-select field into the
// `string[]` of canonical slugs Firestore should hold.
//
// The extractors have not been updated for the crown/crystal split and may
// never be — the Rolex extractor still emits one comma-joined string:
//
//   crownType: "Screw-down, Twinlock double waterproofness system"
//
// That has to keep working, and it has to land as ["screw_down", "twinlock"]
// rather than as one unreadable custom value. So admin ingest resolves it on
// the way in, using the SAME four-pass matcher the migrateCrownTypeCrystal
// sweep uses (`../utils/crownCrystalResolve`) — one source of truth, so a value
// the migration would canonicalize is a value ingest already canonicalized, and
// a freshly imported ref never needs the migration run against it.
//
// (The functions-side copy inside functions/src/migrateCrownTypeCrystal.ts is a
// hand-mirror, not a third implementation: functions/ has `rootDir: "src"` and
// cannot import across the package boundary. That mirror is the standing
// arrangement in this repo — see calibreKey.ts ↔ calibreResolution.ts.)
//
// Three input shapes all normalize to one output shape:
//
//   "screw_down"                      -> ["screw_down"]        (already a slug)
//   ["screw-down", "twinlock"]        -> ["screw_down", ...]   (array in, canonicalized)
//   "Screw-down, Twinlock double …"   -> ["screw_down", "twinlock"]
//
// Anything else (number, object, boolean) is dropped with a warning rather than
// stringified into a junk slug.

// Deliberately does NOT import ./firebase: that module pulls in browser-only
// dependencies, and this one has to be unit-testable under the node test
// environment. The caller reads the lookup and hands the values in, exactly as
// BulkImport already does for buildDialColorResolver / buildDialStyleDetector.

import {
  resolveFieldValues,
  type ResolveCandidate,
} from '../utils/crownCrystalResolve';
import { parseMultiLookupValues } from '../utils/multiLookupValues';

/**
 * The lookup vocabularies set for the current import run, keyed by collection
 * name.
 *
 * Stashed module-side because the ingest mapper (`mapEntryToReference` in
 * BulkImport) is a plain synchronous top-level function called from several
 * spots — exactly the arrangement `dialStyleDetector` and the dial
 * colour/finish resolvers in that file already use. Until set (or if a lookup
 * is unseeded, or its read failed) the vocabulary is empty, nothing resolves,
 * and values fall through to the split-but-raw shape: fails safe, never
 * mis-maps.
 */
const vocabularies = new Map<string, ResolveCandidate[]>();

/** The subset of a lookup value the resolver reads — structurally satisfied by
 *  the client `LookupValue`, so callers can pass `getLookupCollection` output
 *  straight in. */
export interface MultiSelectVocabularyValue {
  value?: string;
  displayName?: string;
  synonyms?: string[];
}

/**
 * Set the vocabulary backing one multi-select field for this import run. Call
 * once before the ingest loop, alongside the dial detectors.
 */
export function setMultiSelectVocabulary(
  collectionName: string,
  values: MultiSelectVocabularyValue[],
): void {
  vocabularies.set(
    collectionName,
    values
      .filter((v) => typeof v.value === 'string' && v.value.trim() !== '')
      .map((v) => ({
        value: v.value,
        displayName: v.displayName,
        synonyms: v.synonyms,
      })),
  );
}

/** Test seam — drop everything set. */
export function clearMultiSelectVocabularies(): void {
  vocabularies.clear();
}

/**
 * The pure half: normalize `raw` against an explicit vocabulary.
 *
 * `label` only names the field in the warning log for a junk shape.
 *
 * Canonicalization is ALL-OR-NOTHING, matching migrateCrownTypeCrystal: if any
 * part fails to resolve, every part is kept as raw text instead of producing a
 * half-canonical array. A half-canonical array reads as curated data and never
 * gets looked at again, whereas raw values show as custom chips in the editor
 * and stay on the migration's triage list — where re-running after adding the
 * missing lookup value fixes them.
 */
export function normalizeMultiSelectAgainst(
  raw: unknown,
  candidates: ResolveCandidate[],
  label = 'multi-select field',
): string[] | undefined {
  if (raw === undefined || raw === null) return undefined;

  if (typeof raw !== 'string' && !Array.isArray(raw)) {
    console.warn(
      `normalizeMultiSelectValue: ${label} received a ${typeof raw}, expected string | string[] — dropping`,
      raw,
    );
    return undefined;
  }

  if (Array.isArray(raw) && raw.some((v) => typeof v !== 'string')) {
    console.warn(
      `normalizeMultiSelectValue: ${label} received an array with non-string entries — dropping those`,
      raw,
    );
  }

  // Shared with every read path: an array is cleaned, a scalar is split on
  // ,/; and non-strings fall out.
  const parts = parseMultiLookupValues(raw);
  if (parts.length === 0) return undefined;

  // An already-canonical array resolves to itself on the exact-slug pass, so
  // running the resolver over array input is a no-op for a well-behaved
  // extractor and an upgrade for one that sends "screw-down".
  const outcome = resolveFieldValues(parts, candidates);
  return outcome.allResolved ? outcome.slugs : parts;
}

/**
 * Normalize one extractor-supplied value for a multi-select field, resolving
 * against the named lookup collection's loaded vocabulary.
 *
 * Returns `undefined` for absent/empty input so `deepStripEmpty` /
 * `removeUndefined` leave the key off the write entirely.
 */
export function normalizeMultiSelectValue(
  raw: unknown,
  collectionName: string,
): string[] | undefined {
  return normalizeMultiSelectAgainst(
    raw,
    vocabularies.get(collectionName) ?? [],
    collectionName,
  );
}
